'use client';

import { useCallback, useEffect, useState } from 'react';

export type Consent = 'pending' | 'accepted' | 'declined';

const STORAGE_KEY = 'jas:consent:v1';

/**
 * Reads/writes the user's analytics-cookie choice. Pending until they
 * resolve the banner. PostHog and Sentry session-replay only initialise
 * when consent === 'accepted' (wired in branch E / future).
 */
export function useConsent(): {
  consent: Consent;
  accept: () => void;
  decline: () => void;
  reset: () => void;
} {
  const [consent, setConsent] = useState<Consent>('pending');

  useEffect(() => {
    if (typeof window === 'undefined') return;
    const v = window.localStorage.getItem(STORAGE_KEY);
    if (v === 'accepted' || v === 'declined') setConsent(v);
  }, []);

  const persist = useCallback((value: Consent) => {
    setConsent(value);
    if (typeof window === 'undefined') return;
    if (value === 'pending') {
      window.localStorage.removeItem(STORAGE_KEY);
    } else {
      window.localStorage.setItem(STORAGE_KEY, value);
    }
  }, []);

  return {
    consent,
    accept: () => persist('accepted'),
    decline: () => persist('declined'),
    reset: () => persist('pending'),
  };
}
